/**
 * Permission helpers for the admin-side audit-log search & export task UI
 * (Issue #287).
 *
 * Mirrors the backend `RequirePermission` decorators on
 * `apps/api/src/modules/audit-log/controllers/audit-log.controller.ts`:
 *   - `audit:read`   gates list + summary.
 *   - `audit:export` gates export task create / list / detail / download-url.
 *
 * Red lines:
 *   - These helpers only decide UI visibility / interactivity. The backend
 *     remains the authoritative gate; tampering with frontend permission
 *     state cannot unlock backend endpoints.
 *   - Callers that lack `audit:read` (and `*:*`) must NOT mount the search
 *     hook, must NOT call `/api/v1/audit-logs`, and must NOT call
 *     `/api/v1/audit-logs/summary`.
 *   - Export creation requires a real second-factor flow; this version of
 *     the UI deliberately exposes only read-only export task listing
 *     (gated by `audit:export`) and never sends a hardcoded
 *     `secondFactorResult=passed`.
 */

export const AUDIT_READ_PERMISSION = 'audit:read';
export const AUDIT_EXPORT_PERMISSION = 'audit:export';
export const SUPER_ADMIN_PERMISSION = '*:*';

function hasAny(
  permissions: readonly string[],
  needles: readonly string[],
): boolean {
  if (permissions.includes(SUPER_ADMIN_PERMISSION)) return true;
  for (const needle of needles) {
    if (permissions.includes(needle)) return true;
  }
  return false;
}

export function canReadAuditLogs(permissions: readonly string[]): boolean {
  return hasAny(permissions, [AUDIT_READ_PERMISSION]);
}

export function canReadAuditLogExports(permissions: readonly string[]): boolean {
  return hasAny(permissions, [AUDIT_EXPORT_PERMISSION]);
}

export function isSuperAdmin(permissions: readonly string[]): boolean {
  return permissions.includes(SUPER_ADMIN_PERMISSION);
}
